import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaChartBar, FaCheckCircle, FaTimesCircle, FaClock, FaFileAlt, FaExclamationTriangle, FaEye, FaTimes, FaCopy, FaFileInvoice, FaUsers, FaFilePdf, FaClipboardList, FaBuilding, FaCalendarCheck, FaHourglassHalf, FaDownload } from 'react-icons/fa';
import {
  getFranchiseeRegistrationReport, getJobsWithoutInvoicesReport, downloadComplianceRegisterPDF,
  getSuppliersList, getWeeklyClientReport, getCompletedJobsPerFranchisee,
  downloadWeeklyClientReportPDF, generateAllWeeklyClientReports, emailWeeklyClientReport,
} from '../services/reportService';
import { getSignedUrl } from '../services/fileService';
import ConfirmationModal from '../components/ConfirmationModal';

// Sentinel supplier-select value for the "generate for every supplier" mode
const ALL_SUPPLIERS_VALUE = '__ALL__';

const generateWeekOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() - i * 7);
    const dayOfWeek = d.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(d);
    monday.setDate(d.getDate() - daysToMonday);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const label = `${monday.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })} \u2013 ${sunday.toLocaleDateString('en-AU', { day: 'numeric' })}`;
    options.push({ value: monday.toISOString(), label });
  }
  return options;
};

const formatDate = (d) => {
  if (!d) return '-';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-AU');
};

const ReportSection = ({ title, icon: Icon, colorClass, headerClass, clients, sectionType }) => {
  const [collapsed, setCollapsed] = useState(false);
  const totalJobs = clients.reduce((sum, c) => sum + c.jobs.length, 0);
  if (clients.length === 0) return null;

  return (
    <div className={`bg-white rounded-lg border shadow-sm overflow-hidden mb-6 ${colorClass}`}>
      <div
        className={`flex items-center justify-between px-4 py-3 cursor-pointer ${headerClass}`}
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-2 font-semibold">
          <Icon />
          {title} ({clients.length} client{clients.length !== 1 ? 's' : ''}, {totalJobs} job{totalJobs !== 1 ? 's' : ''})
        </div>
        <span className="text-lg leading-none">{collapsed ? '▸' : '▾'}</span>
      </div>
      {!collapsed && (
        <div className="p-4 space-y-4">
          {clients.map(client => (
            <div key={client.householdId || `${client.firstName}-${client.lastName}`} className="border rounded-lg overflow-hidden">
              <div className="bg-gray-800 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FaUsers className="text-gray-300 text-sm shrink-0" />
                  <h4 className="text-white font-semibold text-sm truncate">
                    {client.firstName} {client.lastName}
                  </h4>
                  {client.address && <span className="text-xs text-gray-400 truncate hidden sm:inline"> — {client.address}</span>}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-300">
                  <span>{client.jobs.length} job{client.jobs.length !== 1 ? 's' : ''}</span>
                  {client.phoneNumber && <span>{client.phoneNumber}</span>}
                  {client.email && <span>{client.email}</span>}
                </div>
              </div>
              {/* Jobs Table */}
              <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-1.5 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">{sectionType === 'invoiced' ? <><span className="block">Service</span><span className="block">Completed On</span></> : 'Scheduled On'}</th>
                        {sectionType === 'invoiced' && (
                          <th className="px-1.5 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">Scheduled<br/>On</th>
                        )}
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase tracking-wider">FSE</th>
                        {sectionType === 'invoiced' && (
                          <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase tracking-wider">Invoice #</th>
                        )}
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase tracking-wider">Services</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase tracking-wider">Prev</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase tracking-wider">Next</th>
                        <th className="px-2 py-1.5 text-center font-medium text-gray-600 uppercase tracking-wider">0-7</th>
                        <th className="px-2 py-1.5 text-center font-medium text-gray-600 uppercase tracking-wider">8-14</th>
                        <th className="px-2 py-1.5 text-center font-medium text-gray-600 uppercase tracking-wider">14+</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {client.jobs.map(job => (
                        <tr key={job.jobId} className="hover:bg-gray-50">
                          <td className="px-1.5 py-1.5 text-gray-900 whitespace-nowrap text-xs">
                            {sectionType === 'invoiced'
                              ? formatDate(job.completionDate)
                              : formatDate(job.jobDate)}
                          </td>
                          {sectionType === 'invoiced' && (
                            <td className="px-1.5 py-1.5 text-gray-500 whitespace-nowrap text-xs">
                              {formatDate(job.jobDate)}
                            </td>
                          )}
                          <td className="px-2 py-1.5 whitespace-nowrap">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight ${
                              job.status === 'completed' || job.status === 'invoiced'
                                ? job.completedLate
                                  ? 'bg-green-100 text-green-800 ring-1 ring-green-400'
                                  : 'bg-green-100 text-green-800'
                                : job.status === 'cancelled'
                                ? 'bg-gray-100 text-gray-500 line-through'
                                : job.status === 'scheduled'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {job.status}
                              {job.completedLate && <span className="ml-0.5">(late)</span>}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-gray-900 whitespace-nowrap">
                            {job.franchiseeName}
                            {job.franchiseeFseCode && <span className="text-gray-400 ml-0.5">({job.franchiseeFseCode})</span>}
                          </td>
                          {sectionType === 'invoiced' && (
                            <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap font-medium text-xs">
                              {job.invoiceNumber || '-'}
                            </td>
                          )}
                          <td className="px-2 py-1.5 text-gray-600 max-w-[140px] truncate" title={job.services}>
                            {job.services || '-'}
                          </td>
                          <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">
                            {formatDate(job.previousServiceDate)}
                          </td>
                          <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">
                            {formatDate(job.nextServiceDate)}
                          </td>
                        <td className="px-2 py-1.5 text-center">
                          {job.overdueBucket === '0-7' ? (
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${
                              job.completedLate
                                ? 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-400'
                                : 'bg-yellow-100 text-yellow-800'
                            }`} title={job.completedLate ? `Completed ${job.overdueDays}d late` : `Overdue ${job.overdueDays}d`}>
                              {job.overdueDays}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {job.overdueBucket === '8-14' ? (
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${
                              job.completedLate
                                ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-400'
                                : 'bg-orange-100 text-orange-800'
                            }`} title={job.completedLate ? `Completed ${job.overdueDays}d late` : `Overdue ${job.overdueDays}d`}>
                              {job.overdueDays}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {job.overdueBucket === '14+' ? (
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${
                              job.completedLate
                                ? 'bg-red-50 text-red-700 ring-1 ring-red-400'
                                : 'bg-red-100 text-red-800'
                            }`} title={job.completedLate ? `Completed ${job.overdueDays}d late` : `Overdue ${job.overdueDays}d`}>
                              {job.overdueDays}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FranchiseeCard = ({ franchisee }) => {
  const [collapsed, setCollapsed] = useState(false);
  const f = franchisee;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-2 font-semibold text-gray-800">
          <FaBuilding className="text-gray-500" />
          {f.franchiseeName}
          {f.fseCode && <span className="text-gray-400 text-xs">({f.fseCode})</span>}
          <span className="text-gray-400 text-xs font-normal">
            — {f.totalJobs} job{f.totalJobs !== 1 ? 's' : ''}
            {f.completedOnTime > 0 && `, ${f.completedOnTime} on time`}
            {f.completedLate > 0 && `, ${f.completedLate} late`}
          </span>
        </div>
        <span className="text-lg leading-none text-gray-400">{collapsed ? '\u25B8' : '\u25BE'}</span>
      </div>
      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">Client</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">Service Date</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">Completed</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">Invoice Date</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">Status</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">RCTI #</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">Services</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-600 uppercase text-[10px] leading-tight">Alloc</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-600 uppercase text-[10px] leading-tight">0-7</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-600 uppercase text-[10px] leading-tight">8-14</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-600 uppercase text-[10px] leading-tight">14+</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {f.jobs.map(job => (
                <tr key={job.jobId} className="hover:bg-gray-50">
                  <td className="px-2 py-1.5 text-gray-900 whitespace-nowrap">{job.clientName}</td>
                  <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">
                    {job.jobDate ? new Date(job.jobDate).toLocaleDateString('en-AU') : '-'}
                  </td>
                  <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">
                    {job.completionDate ? new Date(job.completionDate).toLocaleDateString('en-AU') : '-'}
                  </td>
                  <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">
                    {job.invoiceDate ? new Date(job.invoiceDate).toLocaleDateString('en-AU') : '-'}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight ${
                      job.completedLate
                        ? 'bg-green-100 text-green-800 ring-1 ring-green-400'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {job.status}
                      {job.completedLate && <span className="ml-0.5">(late)</span>}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap font-medium text-xs">
                    {job.rctiNumber || '-'}
                  </td>
                  <td className="px-2 py-1.5 text-gray-600 max-w-[120px] truncate" title={job.services}>
                    {job.services || '-'}
                  </td>
                  <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{job.allocationType || '-'}</td>
                  <td className="px-2 py-1.5 text-center">
                    {job.overdueBucket === '0-7' ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-100 text-yellow-800 text-[10px] font-bold">
                        {job.overdueDays}
                      </span>
                    ) : <span className="text-gray-300">-</span>}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {job.overdueBucket === '8-14' ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 text-orange-800 text-[10px] font-bold">
                        {job.overdueDays}
                      </span>
                    ) : <span className="text-gray-300">-</span>}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {job.overdueBucket === '14+' ? (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-100 text-red-800 text-[10px] font-bold">
                        {job.overdueDays}
                      </span>
                    ) : <span className="text-gray-300">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ReportsPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('franchisee-registration');

  // Franchisee Registration Report State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    registered: 0,
    notRegistered: 0,
    completeCompliance: 0,
    partialCompliance: 0,
    noCompliance: 0
  });
  const [filterStatus, setFilterStatus] = useState('all'); // all, registered, not-registered
  const [filterCompliance, setFilterCompliance] = useState('all'); // all, complete, partial, none
  const [franchiseeSearch, setFranchiseeSearch] = useState(''); // free-text franchisee search (name / FSE code / email)
  const [selectedFranchisee, setSelectedFranchisee] = useState(null); // For modal
  const [copySuccess, setCopySuccess] = useState(false); // For copy feedback
  const [copiedCount, setCopiedCount] = useState(0); // Number of emails actually copied (excludes suspended)
  const [showDelimiterModal, setShowDelimiterModal] = useState(false); // For delimiter selection modal

  // Jobs Without Invoices Report State
  const [jobsReportLoading, setJobsReportLoading] = useState(false);
  const [jobsReportError, setJobsReportError] = useState(null);
  const [jobsReportData, setJobsReportData] = useState([]);
  const [jobsReportSummary, setJobsReportSummary] = useState({
    totalCompleted: 0,
    totalMissing: 0,
    missingInvoice: 0,
    missingRcti: 0,
    missingBoth: 0
  });
  const [jobsFilterMissingType, setJobsFilterMissingType] = useState('any'); // any, invoice, rcti, both
  const [jobsFilterStartDate, setJobsFilterStartDate] = useState('');
  const [jobsFilterEndDate, setJobsFilterEndDate] = useState('');
  const [jobsReportFetched, setJobsReportFetched] = useState(false);

  // Weekly Client Report Per Supplier State
  const [wcrLoading, setWcrLoading] = useState(false);
  const [wcrError, setWcrError] = useState(null);
  const [wcrSuppliers, setWcrSuppliers] = useState([]);
  const [wcrSelectedSupplierId, setWcrSelectedSupplierId] = useState('');
  const [wcrData, setWcrData] = useState(null);
  const [wcrFetched, setWcrFetched] = useState(false);
  const [wcrSelectedWeek, setWcrSelectedWeek] = useState('');

  // Weekly Client Report — All Suppliers manifest (generate-only, never sends)
  const [wcrManifest, setWcrManifest] = useState(null);
  const [wcrManifestLoading, setWcrManifestLoading] = useState(false);
  const [wcrManifestError, setWcrManifestError] = useState(null);

  // Weekly Client Report — PDF download (single supplier, or one row of the all-suppliers table)
  const [wcrDownloadingId, setWcrDownloadingId] = useState(null);
  const [wcrDownloadError, setWcrDownloadError] = useState(null);

  // Weekly Client Report — email send confirm modal + result. `wcrConfirm` is null when closed;
  // otherwise `{ supplierId, supplierName, email }`. Only ever opened for an individual supplier.
  const [wcrConfirm, setWcrConfirm] = useState(null);
  const [wcrSending, setWcrSending] = useState(false);
  const [wcrSendResult, setWcrSendResult] = useState(null); // { success, supplierName, sentTo|error|code } shown as a banner

  // Completed Jobs Per Franchisee State
  const [cjpfLoading, setCjpfLoading] = useState(false);
  const [cjpfError, setCjpfError] = useState(null);
  const [cjpfData, setCjpfData] = useState(null);
  const [cjpfFetched, setCjpfFetched] = useState(false);
  const [cjpfDays, setCjpfDays] = useState(60);
  const [cjpfStatuses, setCjpfStatuses] = useState(['approved', 'completed', 'invoiced']);
  const [cjpfOverdueBuckets, setCjpfOverdueBuckets] = useState([]);
  const [cjpfFranchiseeId, setCjpfFranchiseeId] = useState('');

  // Compliance Register PDF state
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfSuccess, setPdfSuccess] = useState(false);

  const handleViewDocument = async (fileUrl) => {
    if (!fileUrl) return;
    try {
      const signedUrl = await getSignedUrl(fileUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening compliance document:', err);
    }
  };

  const calculateStats = (data, summary = null) => {
    const registrationSummary = summary?.registration || null;
    const complianceSummary = summary?.compliance || null;

    const total = registrationSummary?.totalAllowedEmails ?? data.length;
    const registered = registrationSummary?.registered ?? data.filter(f => f.registrationStatus === 'registered').length;
    const notRegistered = registrationSummary?.notRegistered ?? data.filter(f => f.registrationStatus === 'new').length;
    const completeCompliance = complianceSummary?.fullyCompliant ?? data.filter(f => f.complianceStatus?.isComplete).length;
    const partialCompliance = complianceSummary?.partiallyCompliant ?? data.filter(f => f.complianceStatus?.hasAny && !f.complianceStatus?.isComplete).length;
    const noCompliance = complianceSummary?.noComplianceUploads ?? data.filter(f => !f.complianceStatus?.hasAny).length;

    setStats({
      total,
      registered,
      notRegistered,
      completeCompliance,
      partialCompliance,
      noCompliance
    });
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getFranchiseeRegistrationReport();
      const data = Array.isArray(result) ? result : (result?.data || []);
      const summary = Array.isArray(result) ? null : (result?.summary || null);
      setReportData(data);
      calculateStats(data, summary);
    } catch (err) {
      setError(err.message || 'Failed to load registration report');
      console.error('Error fetching registration report:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJobsWithoutInvoicesReport = useCallback(async () => {
    setJobsReportLoading(true);
    setJobsReportError(null);
    try {
      const options = {};
      if (jobsFilterStartDate) options.startDate = jobsFilterStartDate;
      if (jobsFilterEndDate) options.endDate = jobsFilterEndDate;
      if (jobsFilterMissingType && jobsFilterMissingType !== 'any') options.missingType = jobsFilterMissingType;

      const result = await getJobsWithoutInvoicesReport(options);
      setJobsReportData(result.data);
      setJobsReportSummary(result.summary);
      setJobsReportFetched(true);
    } catch (err) {
      setJobsReportError(err.message || 'Failed to load jobs without invoices report');
      console.error('Error fetching jobs without invoices report:', err);
    } finally {
      setJobsReportLoading(false);
    }
  }, [jobsFilterStartDate, jobsFilterEndDate, jobsFilterMissingType]);

  const handleJobsReportRefresh = () => {
    setJobsReportFetched(false);
    fetchJobsWithoutInvoicesReport();
  };

  const loadSuppliers = useCallback(async () => {
    try {
      const suppliers = await getSuppliersList();
      setWcrSuppliers(suppliers);
    } catch (err) {
      console.error('Error loading suppliers:', err);
    }
  }, []);

  const fetchWeeklyClientReport = useCallback(async () => {
    if (!wcrSelectedSupplierId) return;
    setWcrLoading(true);
    setWcrError(null);
    try {
      const result = await getWeeklyClientReport(wcrSelectedSupplierId, wcrSelectedWeek || null);
      setWcrData(result);
      setWcrFetched(true);
    } catch (err) {
      setWcrError(err.message || 'Failed to load weekly client report');
      console.error('Error fetching weekly client report:', err);
    } finally {
      setWcrLoading(false);
    }
  }, [wcrSelectedSupplierId, wcrSelectedWeek]);

  // Opens the confirm modal for a single-supplier send.
  const requestEmailSupplier = useCallback((supplierId, supplierName, email) => {
    setWcrSendResult(null);
    setWcrConfirm({ supplierId, supplierName, email: email || null });
  }, []);

  // Downloads the PDF; on success for an individual supplier with an email on file, offers to send it.
  const handleDownloadSupplierPDF = useCallback(async (supplierId, supplierName, weekStartDate, { offerSend = false, email = null } = {}) => {
    setWcrDownloadingId(supplierId);
    setWcrDownloadError(null);
    try {
      const safeName = (supplierName || 'Supplier').replace(/[^a-zA-Z0-9-_]/g, '_');
      await downloadWeeklyClientReportPDF(
        supplierId,
        weekStartDate || null,
        `Weekly_Client_Report_${safeName}${weekStartDate ? `_${weekStartDate}` : ''}.pdf`
      );
      if (offerSend && email) {
        requestEmailSupplier(supplierId, supplierName, email);
      }
    } catch (err) {
      setWcrDownloadError(err.message || 'Failed to download PDF');
      console.error('Error downloading weekly client report PDF:', err);
    } finally {
      setWcrDownloadingId(null);
    }
  }, [requestEmailSupplier]);

  const handleGenerateAllReports = useCallback(async () => {
    setWcrManifestLoading(true);
    setWcrManifestError(null);
    setWcrSendResult(null);
    try {
      const manifest = await generateAllWeeklyClientReports(wcrSelectedWeek || null);
      setWcrManifest(manifest);
    } catch (err) {
      setWcrManifestError(err.message || 'Failed to generate reports for all suppliers');
      console.error('Error generating all weekly client reports:', err);
    } finally {
      setWcrManifestLoading(false);
    }
  }, [wcrSelectedWeek]);

  // The recipient address is never chosen here — it's whatever the server already resolved and
  // showed in the modal (wcrConfirm.email).
  const confirmWcrSend = useCallback(async () => {
    if (!wcrConfirm) return;
    setWcrSending(true);
    try {
      const result = await emailWeeklyClientReport({
        supplierId: wcrConfirm.supplierId,
        weekStartDate: wcrSelectedWeek || null,
        expectedEmail: wcrConfirm.email,
      });
      setWcrSendResult({
        success: true,
        supplierName: result.supplierName || wcrConfirm.supplierName,
        sentTo: result.sentTo,
      });
    } catch (err) {
      setWcrSendResult({
        success: false,
        supplierName: wcrConfirm.supplierName,
        error: err.message || 'Failed to send email',
        code: err.code,
      });
    } finally {
      setWcrSending(false);
      setWcrConfirm(null);
    }
  }, [wcrConfirm, wcrSelectedWeek]);

  const fetchCompletedJobsPerFranchisee = useCallback(async () => {
    setCjpfLoading(true);
    setCjpfError(null);
    try {
      const params = {
        days: cjpfDays,
        statuses: cjpfStatuses.join(','),
      };
      if (cjpfOverdueBuckets.length > 0) {
        params.overdueBuckets = cjpfOverdueBuckets.join(',');
      }
      if (cjpfFranchiseeId) {
        params.franchiseeId = cjpfFranchiseeId;
      }
      const result = await getCompletedJobsPerFranchisee(params);
      setCjpfData(result);
      setCjpfFetched(true);
    } catch (err) {
      setCjpfError(err.message || 'Failed to load completed jobs per franchisee report');
      console.error('Error fetching completed jobs per franchisee report:', err);
    } finally {
      setCjpfLoading(false);
    }
  }, [cjpfDays, cjpfStatuses, cjpfOverdueBuckets, cjpfFranchiseeId]);

  const handleDownloadCompliancePDF = async () => {
    setPdfDownloading(true);
    setPdfError(null);
    setPdfSuccess(false);
    try {
      await downloadComplianceRegisterPDF();
      setPdfSuccess(true);
      setTimeout(() => setPdfSuccess(false), 4000);
    } catch (err) {
      setPdfError(err.message || 'Failed to download PDF');
      console.error('Error downloading compliance register PDF:', err);
    } finally {
      setPdfDownloading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'franchisee-registration') {
      fetchReport();
    } else if (activeTab === 'jobs-without-invoices' && !jobsReportFetched) {
      fetchJobsWithoutInvoicesReport();
    } else if (activeTab === 'weekly-client-report') {
      loadSuppliers();
    } else if (activeTab === 'completed-jobs-per-franchisee' && !cjpfFetched) {
      fetchCompletedJobsPerFranchisee();
    }
  }, [activeTab, jobsReportFetched, fetchReport, fetchJobsWithoutInvoicesReport, loadSuppliers, cjpfFetched, fetchCompletedJobsPerFranchisee]);

  const getFilteredData = () => {
    let filtered = [...reportData];

    // Filter by registration status
    if (filterStatus === 'registered') {
      filtered = filtered.filter(f => f.registrationStatus === 'registered');
    } else if (filterStatus === 'not-registered') {
      filtered = filtered.filter(f => f.registrationStatus === 'new');
    }

    // Filter by compliance status
    if (filterCompliance === 'complete') {
      filtered = filtered.filter(f => f.complianceStatus?.isComplete);
    } else if (filterCompliance === 'partial') {
      filtered = filtered.filter(f => f.complianceStatus?.hasAny && !f.complianceStatus?.isComplete);
    } else if (filterCompliance === 'none') {
      filtered = filtered.filter(f => !f.complianceStatus?.hasAny);
    }

    // Filter by franchisee search (matches name, FSE code or email)
    const term = franchiseeSearch.trim().toLowerCase();
    if (term) {
      filtered = filtered.filter(f =>
        (f.franchiseeName || '').toLowerCase().includes(term) ||
        (f.fseCode || '').toLowerCase().includes(term) ||
        (f.email || '').toLowerCase().includes(term)
      );
    }

    return filtered;
  };

  const getStatusBadge = (status) => {
    if (status === 'registered') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="mr-1" />
          Registered
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <FaClock className="mr-1" />
          Not Registered
        </span>
      );
    }
  };

  const getComplianceBadge = (complianceStatus) => {
    if (!complianceStatus || !complianceStatus.hasAny) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <FaTimesCircle className="mr-1" />
          None (0/4)
        </span>
      );
    } else if (complianceStatus.isComplete) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="mr-1" />
          Complete (4/4)
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <FaExclamationTriangle className="mr-1" />
          Partial ({complianceStatus.completedCount}/{complianceStatus.totalRequired})
        </span>
      );
    }
  };

  const handleCopyEmails = () => {
    setShowDelimiterModal(true);
  };

  const copyWithDelimiter = async (delimiter) => {
    setShowDelimiterModal(false);
    // Exclude suspended franchisees (isActive === false) from the copied email list
    const activeData = getFilteredData().filter(item => item.isActive !== false);
    const emailList = activeData.map(item => item.email).join(delimiter);
    try {
      await navigator.clipboard.writeText(emailList);
      setCopiedCount(activeData.length);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to copy emails:', err);
      setError('Failed to copy emails to clipboard');
    }
  };

  const filteredData = getFilteredData();

  // Filter jobs report data based on local filter (missingType is applied on fetch, this is for client-side additional filtering)
  const getFilteredJobsData = () => {
    return jobsReportData;
  };

  const filteredJobsData = getFilteredJobsData();

  // Get status badge for jobs without invoices report
  const getMissingBadge = (job) => {
    if (!job.hasInvoice && !job.hasRcti) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Both Missing
        </span>
      );
    } else if (!job.hasInvoice) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          Invoice Missing
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          RCTI Missing
        </span>
      );
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-jims-cyan-dark flex items-center">
          <FaChartBar className="mr-3" />
          Reports
        </h1>
      </div>

      {/* Report Tabs */}
      <div className="bg-white rounded-lg shadow-md mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('franchisee-registration')}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition duration-200 ${
                activeTab === 'franchisee-registration'
                  ? 'border-jims-cyan text-jims-cyan-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaUsers className="mr-2" />
              Franchisee Registration
            </button>
            <button
              onClick={() => setActiveTab('jobs-without-invoices')}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition duration-200 ${
                activeTab === 'jobs-without-invoices'
                  ? 'border-jims-cyan text-jims-cyan-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaFileInvoice className="mr-2" />
              Jobs Without Invoices/RCTIs
            </button>
            <button
              onClick={() => setActiveTab('weekly-client-report')}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition duration-200 ${
                activeTab === 'weekly-client-report'
                  ? 'border-jims-cyan text-jims-cyan-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaClipboardList className="mr-2" />
              Weekly Client Report
            </button>
            <button
              onClick={() => setActiveTab('completed-jobs-per-franchisee')}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition duration-200 ${
                activeTab === 'completed-jobs-per-franchisee'
                  ? 'border-jims-cyan text-jims-cyan-dark'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaCheckCircle className="mr-2" />
              Completed Jobs / Franchisee
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'franchisee-registration' && !loading && (
        <div className="sticky top-4 z-20 mb-6">
          <div className="bg-white/95 backdrop-blur rounded-lg shadow-md border border-gray-100 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">Report Actions</p>
              <p className="text-xs text-gray-600">Export the compliance register PDF without leaving this report.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={fetchReport}
                disabled={loading || pdfDownloading}
                className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300 transition duration-300 disabled:opacity-50"
              >
                Refresh Data
              </button>
              <button
                onClick={handleDownloadCompliancePDF}
                disabled={pdfDownloading}
                className="inline-flex items-center px-4 py-2 bg-jims-cyan text-white text-sm font-medium rounded-md hover:bg-jims-cyan-dark transition duration-300 disabled:opacity-50"
              >
                {pdfDownloading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <FaFilePdf className="mr-2" />
                    Export Compliance PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {error && activeTab === 'franchisee-registration' && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}
      {jobsReportError && activeTab === 'jobs-without-invoices' && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p className="font-bold">Error</p>
          <p>{jobsReportError}</p>
        </div>
      )}
      {pdfError && activeTab === 'franchisee-registration' && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p className="font-bold">PDF Export Error</p>
          <p>{pdfError}</p>
        </div>
      )}

      {/* Success Message for Copy */}
      {copySuccess && activeTab === 'franchisee-registration' && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded">
          <p className="font-bold">Success</p>
          <p>Emails copied to clipboard! ({copiedCount} email{copiedCount !== 1 ? 's' : ''}, suspended excluded)</p>
        </div>
      )}
      {pdfSuccess && activeTab === 'franchisee-registration' && (
        <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded">
          <p className="font-bold">Download Started</p>
          <p>The Compliance Register PDF has been downloaded to your device.</p>
        </div>
      )}

      {/* === JOBS WITHOUT INVOICES REPORT === */}
      {activeTab === 'jobs-without-invoices' && (
        <>
          {/* Report Title */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Completed Jobs Without Invoices/RCTIs Report
            </h2>
            <p className="text-gray-600">
              This report shows completed jobs that are missing invoices and/or RCTIs. This can happen due to application errors during generation, monthly allocation jobs pending month end, or car wash jobs that are consolidated monthly.
            </p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Completed</p>
                  <p className="text-3xl font-bold text-gray-800">{jobsReportSummary.totalCompleted}</p>
                </div>
                <div className="bg-gray-100 rounded-full p-3">
                  <FaChartBar className="text-gray-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Missing Invoice</p>
                  <p className="text-3xl font-bold text-orange-600">{jobsReportSummary.missingInvoice}</p>
                </div>
                <div className="bg-orange-100 rounded-full p-3">
                  <FaFileInvoice className="text-orange-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Missing RCTI</p>
                  <p className="text-3xl font-bold text-yellow-600">{jobsReportSummary.missingRcti}</p>
                </div>
                <div className="bg-yellow-100 rounded-full p-3">
                  <FaFileAlt className="text-yellow-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Missing Both</p>
                  <p className="text-3xl font-bold text-red-600">{jobsReportSummary.missingBoth}</p>
                </div>
                <div className="bg-red-100 rounded-full p-3">
                  <FaTimesCircle className="text-red-600 text-2xl" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Missing</p>
                  <p className="text-3xl font-bold text-jims-cyan">{jobsReportSummary.totalMissing}</p>
                </div>
                <div className="bg-jims-cyan bg-opacity-20 rounded-full p-3">
                  <FaExclamationTriangle className="text-jims-cyan text-2xl" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
              <button
                onClick={handleJobsReportRefresh}
                disabled={jobsReportLoading}
                className="inline-flex items-center px-4 py-2 bg-jims-cyan text-white rounded-md hover:bg-jims-cyan-dark transition duration-300 disabled:opacity-50"
              >
                {jobsReportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Loading...
                  </>
                ) : (
                  'Refresh Report'
                )}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Missing Type
                </label>
                <select
                  value={jobsFilterMissingType}
                  onChange={(e) => setJobsFilterMissingType(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan"
                >
                  <option value="any">Any (Invoice or RCTI)</option>
                  <option value="invoice">Invoice Only</option>
                  <option value="rcti">RCTI Only</option>
                  <option value="both">Both Missing</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date (Completion)
                </label>
                <input
                  type="date"
                  value={jobsFilterStartDate}
                  onChange={(e) => setJobsFilterStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date (Completion)
                </label>
                <input
                  type="date"
                  value={jobsFilterEndDate}
                  onChange={(e) => setJobsFilterEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan"
                />
              </div>
            </div>
          </div>

          {/* Report Table */}
          {jobsReportLoading ? (
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex justify-center items-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-jims-cyan mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading report...</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <table className="w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-jims-cyan-dark">
                  <tr>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Job 
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Franchisee
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Completion Date
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Service Type
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Invoice Price
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredJobsData.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                        {jobsReportFetched ? 'No jobs found matching the criteria.' : 'Click "Refresh Report" to load data.'}
                      </td>
                    </tr>
                  ) : (
                    filteredJobsData.map((job, index) => (
                      <tr key={job.jobId || index} className="hover:bg-gray-50">
                        <td className="px-2 py-3 text-sm text-gray-900">
                          <Link
                            to={`/jobs-list/edit/${job.jobId}`}
                            className="text-jims-cyan hover:text-jims-cyan-dark hover:underline font-medium"
                          >
                            <p className="truncate break-words">{job.jobId}</p>
                          </Link>
                          <div className="text-xs text-gray-500">
                            {job.jobDate ? `Job Date: ` + new Date(job.jobDate).toLocaleDateString('en-AU') : '-'}
                          </div>
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-900 break-words">
                          <div>{job.franchiseeFirstName} {job.franchiseeLastName}</div>
                          <div className="text-xs text-gray-500">{job.franchiseeFseCode || ''}</div>
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-900 break-words">
                          {job.client || '-'}
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-900 break-words">
                          {job.supplier || '-'}
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-600">
                          {job.completionDate ? new Date(job.completionDate).toLocaleDateString('en-AU') : '-'}
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-600 break-words">
                          {job.serviceType || '-'}
                        </td>
                        <td className="px-2 py-3 text-sm text-gray-900">
                          {job.invoicePrice != null ? `$${job.invoicePrice.toFixed(2)}` : '-'}
                        </td>
                        <td className="px-2 py-3 text-sm">
                          {getMissingBadge(job)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Total Results */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredJobsData.length} of {jobsReportSummary.totalMissing} jobs with missing invoice/RCTI
          </div>
        </>
      )}

      {/* === WEEKLY CLIENT REPORT PER SUPPLIER === */}
      {activeTab === 'weekly-client-report' && (
        <>
          {/* Report Title */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Weekly Client Report Per Supplier
            </h2>
            <p className="text-gray-600">
              Select a supplier (franchisee) to view their clients and jobs. The report shows service dates, job status, and overdue analysis.
            </p>
          </div>

          {/* Supplier Selector */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Supplier
                </label>
                <select
                  value={wcrSelectedSupplierId}
                  onChange={(e) => {
                    setWcrSelectedSupplierId(e.target.value);
                    setWcrFetched(false);
                    setWcrData(null);
                    setWcrError(null);
                    setWcrManifest(null);
                    setWcrManifestError(null);
                    setWcrDownloadError(null);
                    setWcrSendResult(null);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan"
                >
                  <option value="">-- Select a Supplier --</option>
                  <option value={ALL_SUPPLIERS_VALUE}>All Suppliers</option>
                  {wcrSuppliers.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.clientName}{s.email ? '' : ' (no email on file)'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:w-56">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Week
                </label>
                <select
                  value={wcrSelectedWeek}
                  onChange={(e) => {
                    setWcrSelectedWeek(e.target.value);
                    setWcrFetched(false);
                    setWcrData(null);
                    setWcrError(null);
                    setWcrManifest(null);
                    setWcrManifestError(null);
                    setWcrSendResult(null);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan"
                >
                  <option value="">Current Week</option>
                  {generateWeekOptions().map(w => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </div>
              {wcrSelectedSupplierId === ALL_SUPPLIERS_VALUE ? (
                <button
                  onClick={handleGenerateAllReports}
                  disabled={wcrManifestLoading}
                  className="inline-flex items-center px-6 py-2 bg-jims-cyan text-white rounded-md hover:bg-jims-cyan-dark transition duration-300 disabled:opacity-50"
                >
                  {wcrManifestLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generating all reports...
                    </>
                  ) : (
                    'Generate All Reports'
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={fetchWeeklyClientReport}
                    disabled={!wcrSelectedSupplierId || wcrLoading}
                    className="inline-flex items-center px-6 py-2 bg-jims-cyan text-white rounded-md hover:bg-jims-cyan-dark transition duration-300 disabled:opacity-50"
                  >
                    {wcrLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Loading...
                      </>
                    ) : (
                      'Generate Report'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      const supplier = wcrSuppliers.find(s => s.id === wcrSelectedSupplierId);
                      handleDownloadSupplierPDF(wcrSelectedSupplierId, supplier?.clientName, wcrSelectedWeek || null, { offerSend: true, email: supplier?.email });
                    }}
                    disabled={!wcrSelectedSupplierId || wcrDownloadingId === wcrSelectedSupplierId}
                    className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition duration-300 disabled:opacity-50"
                  >
                    {wcrDownloadingId === wcrSelectedSupplierId ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <FaDownload className="mr-2" />
                        Download PDF
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
            {wcrSelectedSupplierId === ALL_SUPPLIERS_VALUE && (
              <p className="text-xs text-gray-500 mt-3">
                Generates one PDF per supplier — this only builds the PDFs, it never sends anything. Large supplier lists can take a few minutes.
              </p>
            )}
            {wcrSelectedSupplierId && wcrSelectedSupplierId !== ALL_SUPPLIERS_VALUE && !wcrSuppliers.find(s => s.id === wcrSelectedSupplierId)?.email && (
              <p className="text-xs text-red-600 mt-3">No email on file for this supplier — email sending is disabled. Only "Download PDF" is available.</p>
            )}
          </div>

          {/* Error Messages */}
          {wcrError && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
              <p className="font-bold">Error</p>
              <p>{wcrError}</p>
            </div>
          )}
          {wcrManifestError && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
              <p className="font-bold">Error</p>
              <p>{wcrManifestError}</p>
            </div>
          )}
          {wcrDownloadError && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
              <p className="font-bold">PDF Download Error</p>
              <p>{wcrDownloadError}</p>
            </div>
          )}

          {/* Send Result */}
          {wcrSendResult && (() => {
            const banner = wcrSendResult.success
              ? { type: 'success', heading: 'Sent', text: `Weekly client report emailed to ${wcrSendResult.supplierName} at ${wcrSendResult.sentTo}.` }
              : {
                type: 'error',
                heading: 'Not sent',
                text: wcrSendResult.code === 'no-email'
                  ? `${wcrSendResult.supplierName} has no email on file — nothing was sent.`
                  : wcrSendResult.code === 'email-changed'
                    ? `${wcrSendResult.supplierName}'s email on file changed since this send was confirmed — nothing was sent. Re-open and confirm again.`
                    : (wcrSendResult.error || 'Failed to send email.'),
              };
            return (
              <div className={`p-4 mb-6 rounded border-l-4 ${banner.type === 'success' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-red-100 border-red-500 text-red-700'}`}>
                <p className="font-bold">{banner.heading}</p>
                <p>{banner.text}</p>
              </div>
            );
          })()}

          {wcrSelectedSupplierId !== ALL_SUPPLIERS_VALUE ? (
            <>
              {/* Report Content */}
              {wcrLoading ? (
                <div className="bg-white rounded-lg shadow-md p-8">
                  <div className="flex justify-center items-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-jims-cyan mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading report...</p>
                    </div>
                  </div>
                </div>
              ) : wcrFetched && wcrData ? (
                <>
                  {/* Supplier Info */}
                  <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <FaBuilding className="text-jims-cyan text-xl" />
                      <h3 className="text-xl font-bold text-gray-800">
                        {wcrData.supplier.clientName}
                      </h3>
                    </div>
                  </div>

                  {/* Statistics Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm font-medium">Invoiced This Week</p>
                          <p className="text-3xl font-bold text-green-600">{wcrData.summary.invoicedThisWeek}</p>
                        </div>
                        <div className="bg-green-100 rounded-full p-3">
                          <FaCheckCircle className="text-green-600 text-2xl" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm font-medium">Due Next Week</p>
                          <p className="text-3xl font-bold text-blue-600">{wcrData.summary.dueNextWeek}</p>
                        </div>
                        <div className="bg-blue-100 rounded-full p-3">
                          <FaCalendarCheck className="text-blue-600 text-2xl" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-gray-500 text-sm font-medium">Services to be actioned</p>
                          <p className="text-3xl font-bold text-red-600">{wcrData.summary.overdue}</p>
                        </div>
                        <div className="bg-red-100 rounded-full p-3">
                          <FaHourglassHalf className="text-red-600 text-2xl" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Report Sections */}
                  <ReportSection
                    title="Invoiced This Week"
                    icon={FaCheckCircle}
                    colorClass="border-green-300"
                    headerClass="bg-green-50 text-green-800"
                    clients={wcrData.invoicedThisWeek}
                    sectionType="invoiced"
                  />
                  <ReportSection
                    title="Due Next Week"
                    icon={FaCalendarCheck}
                    colorClass="border-blue-300"
                    headerClass="bg-blue-50 text-blue-800"
                    clients={wcrData.dueNextWeek}
                    sectionType="due"
                  />
                  <ReportSection
                    title="Services to be actioned"
                    icon={FaHourglassHalf}
                    colorClass="border-red-300"
                    headerClass="bg-red-50 text-red-800"
                    clients={wcrData.overdue}
                    sectionType="overdue"
                  />

                  {/* Total Results */}
                  <div className="mt-4 text-sm text-gray-600">
                    Showing {wcrData.invoicedThisWeek.length + wcrData.dueNextWeek.length + wcrData.overdue.length} client{((wcrData.invoicedThisWeek.length + wcrData.dueNextWeek.length + wcrData.overdue.length) !== 1 ? 's' : '')} across 3 sections
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
                  Select a supplier and click "Generate Report" to view the weekly client report.
                </div>
              )}
            </>
          ) : (
            <>
              {/* All Suppliers manifest */}
              {wcrManifestLoading ? (
                <div className="bg-white rounded-lg shadow-md p-8">
                  <div className="flex justify-center items-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-jims-cyan mx-auto mb-4"></div>
                      <p className="text-gray-600">Generating PDFs for every supplier — this can take a few minutes, please don't navigate away.</p>
                    </div>
                  </div>
                </div>
              ) : wcrManifest ? (
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{wcrManifest.weekStart} – {wcrManifest.weekEnd}</h3>
                      <p className="text-sm text-gray-600">
                        {wcrManifest.suppliers.length} supplier{wcrManifest.suppliers.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-200">
                          <th className="py-2 pr-4">Supplier</th>
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">Jobs</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {wcrManifest.suppliers.map(row => (
                          <tr key={row.supplierId} className="border-b border-gray-100">
                            <td className="py-2 pr-4 font-medium text-gray-800">{row.supplierName}</td>
                            <td className="py-2 pr-4">
                              {row.email ? (
                                <span className="text-gray-700">{row.email}</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">No email</span>
                              )}
                            </td>
                            <td className="py-2 pr-4 text-gray-600">{row.totalJobs}</td>
                            <td className="py-2 pr-4 text-gray-600">
                              {row.generated ? 'Ready' : (row.reason === 'no-jobs' ? 'No jobs this week' : (row.reason || 'Not generated'))}
                            </td>
                            <td className="py-2 pr-4">
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={() => handleDownloadSupplierPDF(row.supplierId, row.supplierName, wcrManifest.weekStart)}
                                  disabled={!row.generated || wcrDownloadingId === row.supplierId}
                                  className="px-3 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50 text-xs"
                                >
                                  {wcrDownloadingId === row.supplierId ? '...' : 'Download'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
                  Click "Generate All Reports" to build a PDF for every supplier and see who has an email on file.
                </div>
              )}
            </>
          )}

          {/* Post-download send prompt — recipient address always comes from the server, never from this UI.
              Only ever opened after a successful individual-supplier download with an email on file. */}
          <ConfirmationModal
            isOpen={!!wcrConfirm}
            onClose={() => setWcrConfirm(null)}
            onConfirm={confirmWcrSend}
            title="Send report to supplier?"
            message={
              <span className="block">
                The PDF just downloaded is <strong>{wcrConfirm?.supplierName}</strong>'s weekly client report. Send this same report to <strong>{wcrConfirm?.email}</strong>?
              </span>
            }
            isLoading={wcrSending}
            loadingText="Sending…"
            confirmText="Send"
            variant="danger"
          />
        </>
      )}

      {/* === COMPLETED JOBS PER FRANCHISEE === */}
      {activeTab === 'completed-jobs-per-franchisee' && (
        <>
          {/* Report Title */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Completed Jobs Per Franchisee
            </h2>
            <p className="text-gray-600">
              Shows completed and invoiced jobs grouped by franchisee. Use the filters below to refine by date range, status, and overdue bucket.
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              {/* Days filter */}
              <div className="md:w-40">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={cjpfDays}
                  onChange={(e) => setCjpfDays(Number(e.target.value) || 60)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan"
                />
              </div>

              {/* Status filter */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Status
                </label>
                <div className="flex flex-wrap gap-2">
                  {['approved', 'completed', 'invoiced'].map(status => (
                    <label key={status} className="inline-flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={cjpfStatuses.includes(status)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCjpfStatuses(prev => [...prev, status]);
                          } else {
                            setCjpfStatuses(prev => prev.filter(s => s !== status));
                          }
                        }}
                        className="rounded border-gray-300 text-jims-cyan focus:ring-jims-cyan"
                      />
                      <span className="capitalize">{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Overdue bucket filter */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overdue Filter
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'on-time', label: 'On Time', color: 'text-green-700' },
                    { value: '0-7', label: '0-7 days', color: 'text-yellow-700' },
                    { value: '8-14', label: '8-14 days', color: 'text-orange-700' },
                    { value: '14+', label: '14+ days', color: 'text-red-700' },
                  ].map(bucket => (
                    <label key={bucket.value} className={`inline-flex items-center gap-1.5 text-sm ${bucket.color}`}>
                      <input
                        type="checkbox"
                        checked={cjpfOverdueBuckets.includes(bucket.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCjpfOverdueBuckets(prev => [...prev, bucket.value]);
                          } else {
                            setCjpfOverdueBuckets(prev => prev.filter(b => b !== bucket.value));
                          }
                        }}
                        className="rounded border-gray-300 text-jims-cyan focus:ring-jims-cyan"
                      />
                      {bucket.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Franchisee filter */}
              {cjpfData && cjpfData.franchisees && cjpfData.franchisees.length > 0 && (
                <div className="md:w-64">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Franchisee
                  </label>
                  <select
                    value={cjpfFranchiseeId}
                    onChange={(e) => setCjpfFranchiseeId(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan text-sm"
                  >
                    <option value="">All Franchisees</option>
                    {cjpfData.franchisees.map(f => (
                      <option key={f.franchiseeId} value={f.franchiseeId}>
                        {f.franchiseeName}{f.fseCode ? ` (${f.fseCode})` : ''} — {f.totalJobs} job{f.totalJobs !== 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Generate button */}
              <button
                onClick={fetchCompletedJobsPerFranchisee}
                disabled={cjpfLoading || cjpfStatuses.length === 0}
                className="inline-flex items-center px-6 py-2 bg-jims-cyan text-white rounded-md hover:bg-jims-cyan-dark transition duration-300 disabled:opacity-50"
              >
                {cjpfLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Loading...
                  </>
                ) : (
                  'Generate Report'
                )}
              </button>
            </div>
          </div>

          {/* Error */}
          {cjpfError && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
              <p className="font-bold">Error</p>
              <p>{cjpfError}</p>
            </div>
          )}

          {/* Loading */}
          {cjpfLoading ? (
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex justify-center items-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-jims-cyan mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading report...</p>
                </div>
              </div>
            </div>
          ) : cjpfFetched && cjpfData ? (
            <>
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Total Jobs</p>
                      <p className="text-3xl font-bold text-gray-800">{cjpfData.summary.totalJobs}</p>
                    </div>
                    <div className="bg-gray-100 rounded-full p-3">
                      <FaChartBar className="text-gray-600 text-2xl" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Franchisees</p>
                      <p className="text-3xl font-bold text-blue-600">{cjpfData.summary.totalFranchisees}</p>
                    </div>
                    <div className="bg-blue-100 rounded-full p-3">
                      <FaUsers className="text-blue-600 text-2xl" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">On Time</p>
                      <p className="text-3xl font-bold text-green-600">{cjpfData.summary.completedOnTime}</p>
                    </div>
                    <div className="bg-green-100 rounded-full p-3">
                      <FaCheckCircle className="text-green-600 text-2xl" />
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Completed Late</p>
                      <p className="text-3xl font-bold text-red-600">{cjpfData.summary.completedLate}</p>
                    </div>
                    <div className="bg-red-100 rounded-full p-3">
                      <FaClock className="text-red-600 text-2xl" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Franchisee Cards (collapsible) */}
              {cjpfData.franchisees.length === 0 ? (
                <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
                  No completed jobs found matching the selected filters.
                </div>
              ) : (
                <div className="space-y-4">
                  {cjpfData.franchisees.map(f => (
                    <FranchiseeCard key={f.franchiseeId} franchisee={f} />
                  ))}
                </div>
              )}

              {/* Total Results */}
              <div className="mt-4 text-sm text-gray-600">
                Showing {cjpfData.summary.totalFranchisees} franchisee{cjpfData.summary.totalFranchisees !== 1 ? 's' : ''} with {cjpfData.summary.totalJobs} job{cjpfData.summary.totalJobs !== 1 ? 's' : ''} in the past {cjpfDays} days
              </div>
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
              Set your filters and click "Generate Report" to load results.
            </div>
          )}
        </>
      )}

      {/* === FRANCHISEE REGISTRATION REPORT === */}
      {activeTab === 'franchisee-registration' && (
        <>
          {/* Loading State */}
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex justify-center items-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-jims-cyan mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading report...</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Report Title */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Franchisee Registration & Compliance Status Report
                </h2>
                <p className="text-gray-600">
                  This report shows the registration status of all allowed franchisee emails and their compliance document upload status.
                </p>
              </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Emails</p>
              <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
            </div>
            <div className="bg-gray-100 rounded-full p-3">
              <FaChartBar className="text-gray-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Registered</p>
              <p className="text-3xl font-bold text-green-600">{stats.registered}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <FaCheckCircle className="text-green-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Not Registered</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.notRegistered}</p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <FaClock className="text-yellow-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Complete</p>
              <p className="text-3xl font-bold text-green-600">{stats.completeCompliance}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <FaCheckCircle className="text-green-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Partial</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.partialCompliance}</p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <FaExclamationTriangle className="text-yellow-600 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">None</p>
              <p className="text-3xl font-bold text-red-600">{stats.noCompliance}</p>
            </div>
            <div className="bg-red-100 rounded-full p-3">
              <FaTimesCircle className="text-red-600 text-2xl" />
            </div>
          </div>
        </div>
      </div>
      <div className="mb-6 text-xs text-gray-500">
        Compliance totals are calculated from active franchisee records (same source used by Compliance PDF export).
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
          <button
            onClick={handleCopyEmails}
            className="inline-flex items-center px-4 py-2 bg-jims-cyan text-white rounded-md hover:bg-jims-cyan-dark transition duration-300"
          >
            <FaCopy className="mr-2" />
            {copySuccess ? 'Copied!' : 'Copy All Emails'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Franchisee
            </label>
            <input
              type="text"
              value={franchiseeSearch}
              onChange={(e) => setFranchiseeSearch(e.target.value)}
              placeholder="Search name, FSE code or email…"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Registration Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan"
            >
              <option value="all">All</option>
              <option value="registered">Registered</option>
              <option value="not-registered">Not Registered</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Compliance Status
            </label>
            <select
              value={filterCompliance}
              onChange={(e) => setFilterCompliance(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-jims-cyan focus:border-jims-cyan"
            >
              <option value="all">All</option>
              <option value="complete">Complete (4/4)</option>
              <option value="partial">Partial</option>
              <option value="none">None (0/4)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-jims-cyan-dark">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Registration Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Compliance Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  FSE Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Franchisee Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Added Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No data matches the selected filters.
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getStatusBadge(item.registrationStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getComplianceBadge(item.complianceStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.fseCode || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.franchiseeName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.addedDate ? new Date(item.addedDate).toLocaleDateString('en-AU') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {item.registrationStatus === 'registered' && item.complianceStatus?.hasAny ? (
                        <button
                          onClick={() => setSelectedFranchisee(item)}
                          className="inline-flex items-center px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200"
                        >
                          <FaEye className="mr-1" />
                          View Details
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Total Results */}
      <div className="mt-4 text-sm text-gray-600">
        Showing {filteredData.length} of {stats.total} total records
      </div>

      {/* Compliance Details Modal */}
      {selectedFranchisee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                Compliance Details
              </h3>
              <button
                onClick={() => setSelectedFranchisee(null)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-6">
              {/* Franchisee Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-3">Franchisee Information</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-medium text-gray-900">{selectedFranchisee.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">FSE Code:</span>
                    <p className="font-medium text-gray-900">{selectedFranchisee.fseCode || 'N/A'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Franchisee Name:</span>
                    <p className="font-medium text-gray-900">{selectedFranchisee.franchiseeName || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Overall Status */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Overall Compliance Status</h4>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">
                    {selectedFranchisee.complianceStatus.completedCount} / {selectedFranchisee.complianceStatus.totalRequired}
                  </span>
                  <div>
                    {selectedFranchisee.complianceStatus.isComplete ? (
                      <span className="text-green-600 font-semibold flex items-center">
                        <FaCheckCircle className="mr-2" /> Complete
                      </span>
                    ) : (
                      <span className="text-yellow-600 font-semibold flex items-center">
                        <FaExclamationTriangle className="mr-2" /> Partial
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Compliance Items */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Compliance Items</h4>
                <div className="space-y-3">
                  
                  {/* Police Certificate */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h5 className="font-medium text-gray-900">1. Police Check Certificate</h5>
                          {selectedFranchisee.complianceStatus.details.policeCertificate ? (
                            <FaCheckCircle className="ml-2 text-green-500" />
                          ) : (
                            <FaTimesCircle className="ml-2 text-red-500" />
                          )}
                        </div>
                        {selectedFranchisee.complianceStatus.details.policeCertificate ? (
                          <div className="text-sm space-y-1">
                            {selectedFranchisee.complianceStatus.compliance?.policeCertificate && (
                              <button
                                type="button"
                                onClick={() => handleViewDocument(selectedFranchisee.complianceStatus.compliance.policeCertificate)}
                                className="text-blue-600 hover:underline flex items-center"
                              >
                                <FaFileAlt className="mr-1" /> View Document
                              </button>
                            )}
                            {selectedFranchisee.complianceStatus.compliance?.policeCertificateExpiryDate && (
                              <p className="text-gray-600">
                                Expires: {new Date(selectedFranchisee.complianceStatus.compliance.policeCertificateExpiryDate).toLocaleDateString('en-AU')}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-red-600">Not uploaded</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Certificate of Currency */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h5 className="font-medium text-gray-900">2. Certificate of Currency</h5>
                          {selectedFranchisee.complianceStatus.details.certificateOfCurrency ? (
                            <FaCheckCircle className="ml-2 text-green-500" />
                          ) : (
                            <FaTimesCircle className="ml-2 text-red-500" />
                          )}
                        </div>
                        {selectedFranchisee.complianceStatus.details.certificateOfCurrency ? (
                          <div className="text-sm space-y-1">
                            {selectedFranchisee.complianceStatus.compliance?.certificateOfCurrency && (
                              <button
                                type="button"
                                onClick={() => handleViewDocument(selectedFranchisee.complianceStatus.compliance.certificateOfCurrency)}
                                className="text-blue-600 hover:underline flex items-center"
                              >
                                <FaFileAlt className="mr-1" /> View Document
                              </button>
                            )}
                            {selectedFranchisee.complianceStatus.compliance?.certificateOfCurrencyStartDate && 
                             selectedFranchisee.complianceStatus.compliance?.certificateOfCurrencyFinishDate && (
                              <p className="text-gray-600">
                                Period: {new Date(selectedFranchisee.complianceStatus.compliance.certificateOfCurrencyStartDate).toLocaleDateString('en-AU')} - {new Date(selectedFranchisee.complianceStatus.compliance.certificateOfCurrencyFinishDate).toLocaleDateString('en-AU')}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-red-600">Not uploaded</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Influenza Vaccine */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h5 className="font-medium text-gray-900">3. Influenza Vaccine Declaration</h5>
                          {selectedFranchisee.complianceStatus.details.influenzaVaccine ? (
                            <FaCheckCircle className="ml-2 text-green-500" />
                          ) : (
                            <FaTimesCircle className="ml-2 text-red-500" />
                          )}
                        </div>
                        {selectedFranchisee.complianceStatus.details.influenzaVaccine ? (
                          <div className="text-sm space-y-1">
                            <p className="text-green-600 font-medium">✓ Declared</p>
                            {selectedFranchisee.complianceStatus.compliance?.influenzaVaccineDate && (
                              <p className="text-gray-600">
                                Vaccination Date: {new Date(selectedFranchisee.complianceStatus.compliance.influenzaVaccineDate).toLocaleDateString('en-AU')}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-red-600">Not declared</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* LCHS Fact Sheet */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h5 className="font-medium text-gray-900">4. Aged Care Provider Training</h5>
                          {selectedFranchisee.complianceStatus.details.lchsFactSheet ? (
                            <FaCheckCircle className="ml-2 text-green-500" />
                          ) : (
                            <FaTimesCircle className="ml-2 text-red-500" />
                          )}
                        </div>
                        {selectedFranchisee.complianceStatus.details.lchsFactSheet ? (
                          <p className="text-sm text-green-600 font-medium">✓ Acknowledged</p>
                        ) : (
                          <p className="text-sm text-red-600">Not acknowledged</p>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setSelectedFranchisee(null)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
            </>
          )}
        </>
      )}

      {/* Delimiter Selection Modal */}
      {showDelimiterModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowDelimiterModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl p-6 w-96 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-1">
              <div>
                <h3 className="text-base font-semibold text-gray-900">Copy Emails</h3>
                <p className="text-xs text-gray-400 mt-0.5">Choose how addresses will be separated</p>
              </div>
              <button
                onClick={() => setShowDelimiterModal(false)}
                className="text-gray-300 hover:text-gray-500 transition ml-4 mt-0.5 flex-shrink-0"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Option Cards */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              {[
                { delimiter: ', ', symbol: ',', label: 'Comma', preview: 'jane@email.com,\nalex@email.com' },
                { delimiter: '; ', symbol: ';', label: 'Semicolon', preview: 'jane@email.com;\nalex@email.com' },
              ].map(({ delimiter, symbol, label, preview }) => (
                <button
                  key={label}
                  onClick={() => copyWithDelimiter(delimiter)}
                  className="group relative flex flex-col items-center text-center p-4 rounded-xl border-2 border-gray-100 hover:border-jims-cyan hover:bg-cyan-50 transition-all duration-150 cursor-pointer focus:outline-none focus:border-jims-cyan"
                >
                  <span className="text-4xl font-mono font-bold text-gray-300 group-hover:text-jims-cyan transition-colors duration-150 leading-none mb-2">
                    {symbol}
                  </span>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-jims-cyan transition-colors duration-150">
                    {label}
                  </span>
                  <span className="mt-2 text-xs text-gray-300 group-hover:text-cyan-400 font-mono leading-relaxed transition-colors duration-150 whitespace-pre">
                    {preview}
                  </span>
                </button>
              ))}
            </div>

            {/* Cancel */}
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowDelimiterModal(false)}
                className="text-xs text-gray-400 hover:text-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
